/**
 * useLabTheme — lê/escreve o tema local do module_lab.
 *
 * Decisão v1 (DESIGN_TOKENS.md §Decisões): cada participante escolhe o
 * próprio tema; não segue prefers-color-scheme automático.
 * Persistência em localStorage['module_lab:theme'].
 *
 * Default: 'light'. Valores: 'light' | 'dark'.
 */

import { useCallback, useEffect, useState } from 'react'

export type LabTheme = 'light' | 'dark'

const STORAGE_KEY = 'module_lab:theme'

function readStored(): LabTheme {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    if (v === 'dark' || v === 'light') return v
  } catch {
    /* ignora */
  }
  return 'light'
}

export function useLabTheme() {
  const [theme, setThemeState] = useState<LabTheme>(() => readStored())

  const setTheme = useCallback((t: LabTheme) => {
    setThemeState(t)
    try {
      localStorage.setItem(STORAGE_KEY, t)
    } catch {
      /* quota cheia */
    }
  }, [])

  const toggle = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark')
  }, [theme, setTheme])

  // Sincroniza entre abas (mesmo aluno / mestre em múltiplas janelas).
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && (e.newValue === 'dark' || e.newValue === 'light')) {
        setThemeState(e.newValue)
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  return { theme, setTheme, toggle }
}
